import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 20000,
});

// ============================================================================
// In-memory GET cache
// ----------------------------------------------------------------------------
// Our upstream ingress stamps `Cache-Control: no-store` on every response,
// which means the browser refetches everything from scratch on every route
// change. That makes nav feel sluggish, especially on mobile.
//
// This lightweight cache serves repeat GETs to the SAME URL from RAM within
// a short TTL, so tapping a nav item -> back -> nav item again is instant.
//
// Rules:
//  - Only GET requests are cached.
//  - Only when the caller has NOT set `cache: false` on the request config.
//  - Any admin write (POST/PATCH/PUT/DELETE) blows the whole cache.
//  - TTL is short (60s) so users still see fresh data within a session.
//  - Skips URLs that look admin/auth-related to be extra safe.
// ============================================================================
const CACHE_TTL_MS = 60_000;
const _cache = new Map(); // key -> { ts, data, status, headers }
const _inflight = new Map(); // key -> Promise resolving to axios response

const _cacheKey = (config) => {
  const params = config.params ? JSON.stringify(config.params) : "";
  return `${(config.baseURL || "") + (config.url || "")}::${params}`;
};

const _shouldSkipCache = (config) => {
  if (config.cache === false) return true;
  const url = config.url || "";
  if (/\/admin\b/i.test(url)) return true;
  if (config.headers && (config.headers["X-Admin-Password"] || config.headers["x-admin-password"])) return true;
  return false;
};

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" || _shouldSkipCache(config)) return config;
  const key = _cacheKey(config);
  // (1) Cache hit → resolve immediately without touching the network.
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < CACHE_TTL_MS) {
    config.adapter = () =>
      Promise.resolve({
        data: hit.data,
        status: hit.status,
        statusText: "OK (from cache)",
        headers: hit.headers,
        config,
        request: null,
      });
    return config;
  }
  // (2) In-flight dedupe → piggyback on the pending request for the same key.
  const pending = _inflight.get(key);
  if (pending) {
    config.adapter = () => pending.then((r) => ({ ...r, config, statusText: "OK (deduped)" }));
    return config;
  }
  // (3) Mark this request as in-flight; the response interceptor will clear it.
  let resolveInflight;
  const pendingPromise = new Promise((res) => { resolveInflight = res; });
  _inflight.set(key, pendingPromise);
  config._resolveInflight = resolveInflight;
  return config;
});

api.interceptors.response.use(
  (response) => {
    const method = (response.config.method || "get").toLowerCase();
    if (method === "get" && !_shouldSkipCache(response.config)) {
      const key = _cacheKey(response.config);
      if (response.status === 200 && response.statusText !== "OK (deduped)") {
        _cache.set(key, {
          ts: Date.now(),
          data: response.data,
          status: response.status,
          headers: response.headers,
        });
      }
      if (response.config._resolveInflight) {
        response.config._resolveInflight(response);
      }
      _inflight.delete(key);
    } else if (method !== "get" && response.status >= 200 && response.status < 300) {
      // Any successful write invalidates cached GETs — the next read will be
      // fresh. Cheaper than trying to be surgical about which URLs to blow.
      _cache.clear();
    }
    return response;
  },
  (error) => {
    try {
      const cfg = error.config;
      if (cfg) {
        const key = _cacheKey(cfg);
        _inflight.delete(key);
      }
    } catch { /* noop */ }
    return Promise.reject(error);
  },
);

export const clearApiCache = () => {
  _cache.clear();
  _inflight.clear();
};

export const formatDate = (iso, opts = { weekday: "short", month: "short", day: "numeric" }) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", opts);
  } catch {
    return iso;
  }
};

export const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

// Resolve image URLs that may be relative paths from our upload endpoint
// (e.g. "/api/uploads/abc.jpg") into absolute backend URLs.
// Route full-size ESPN images (500-1000px PNGs, often 100-300KB) through
// ESPN's combiner CDN so they arrive resized for their actual render size.
export const espnImg = (u, w = 96) => {
  if (!u || !u.includes("espncdn.com") || u.includes("/combiner/")) return u;
  try {
    const { pathname } = new URL(u);
    return `https://a.espncdn.com/combiner/i?img=${pathname}&w=${w}&h=${w}`;
  } catch {
    return u;
  }
};

export const resolveImg = (u) => {
  if (!u) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};
